import { useState, useRef, useEffect } from 'react';

interface SearchBarProps {
  query: string;
  onSearch: (query: string) => void;
  placeholder?: string;
}

export default function SearchBar({
  query,
  onSearch,
  placeholder = 'Search...',
}: SearchBarProps) {
  const [isFocused, setIsFocused] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const toggleSearchBar = () => {
    setIsExpanded(!isExpanded);
    if (!isExpanded) {
      // Focus the input when expanding
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    } else {
      // Clear the search when collapsing
      onSearch('');
    }
  };

  // Close search bar when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        if (isExpanded && !query) {
          setIsExpanded(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isExpanded, query]);

  return (
    <div ref={containerRef} className="flex items-center w-full">
      {/* Search Icon - Always visible */}
      <button
        onClick={toggleSearchBar}
        className="p-2 text-gray-500 hover:text-custom-stone transition-colors duration-300 focus:outline-none"
        aria-label="Toggle search"
      >
        <svg
          className="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
          />
        </svg>
      </button>

      {/* Search Input - Expandable */}
      <div
        className={`flex items-center transition-all duration-300 ease-in-out overflow-hidden ${
          isExpanded ? 'w-full ml-2' : 'w-0'
        }`}
      >
        <div
          className={`flex items-center w-full border rounded-lg p-2 transition-colors duration-300 ${
            isFocused ? 'border-custom-stone' : 'border-gray-300'
          }`}
        >
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => onSearch(e.target.value)}
            placeholder={placeholder}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            className="flex-grow border-none outline-none"
          />

          {/* Clear Button */}
          {query && (
            <button
              onClick={() => onSearch('')}
              className="ml-2 text-gray-400 hover:text-red-600 focus:outline-none"
            >
              &#10005; {/* The "X" icon */}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
